/* Circadia Favourites — heart toggle (FAVORITES.md)
 *
 * Powers the "save for later" heart on PDPs, product cards, journal articles,
 * journal archive cards, playbook pages, and playbook archive cards. Companion
 * to the dashboard Favourites tab (rendered by customer-dashboard.js, which
 * lists + removes).
 *
 * Two responsibilities:
 *   1. Paint saved state — one authed GET of the logged-in customer's
 *      favourites (loaded lazily the first time an authed heart is seen), then
 *      mark every [data-fav-toggle] favorited/not. The saved-set is kept in
 *      memory and re-applied to hearts that appear LATER — the playbook archive
 *      hydrates its cards via JS after an async fetch, so its hearts don't exist
 *      at boot. A MutationObserver re-scans when new hearts are injected.
 *   2. Toggle on activate — optimistic flip, then POST /toggle and reconcile
 *      from the server's {favorited} (revert on failure), keeping the saved-set
 *      in sync so a later re-scan won't clobber the new state. A logged-out
 *      heart (data-fav-guest) redirects to login instead.
 *
 * Hearts are normally <button>; inside whole-card <a> anchors (journal archive
 * cards) they render as <span role="button" tabindex="0"> — a <button> nested
 * in an <a> is invalid HTML. Click works for both via delegation; this file
 * adds Enter/Space activation for the span (native buttons already do that).
 *
 * Same App-Proxy pattern as customer-dashboard.js: same-origin fetch to
 * /apps/dashboard/customer/favorites* (Shopify forwards the signed
 * logged_in_customer_id; no CORS, no Authorization header). Note the route uses
 * American "favorites" (backend/DB); the UI copy uses British "favourites".
 *
 * Style follows circadia-chrome.js: vanilla JS, IIFE, document-level delegation,
 * no framework, no build step.
 */
(function () {
  'use strict';

  var BASE = '/apps/dashboard/customer/favorites';

  // Saved-set of the logged-in customer's favourites, "type:id" → true. null
  // until the GET resolves. loadState guards the fetch to exactly once.
  var saved = null;
  var loadState = 'idle'; // 'idle' | 'loading' | 'done' | 'error'

  function $$(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function key(type, id) {
    return type + ':' + id;
  }

  function buttonKey(btn) {
    return key(
      btn.getAttribute('data-fav-type'),
      btn.getAttribute('data-fav-id')
    );
  }

  function setBusy(btn, busy) {
    if (busy) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function loginRedirect() {
    var ret = encodeURIComponent(
      window.location.pathname + window.location.search + window.location.hash
    );
    window.location.href = '/account/login?return_url=' + ret;
  }

  // Paint every live heart matching a key (re-query each call so async-injected
  // hearts stay in sync). Only flips the matched key — never clobbers others.
  function paintKey(k, favorited) {
    $$('[data-fav-toggle]').forEach(function (btn) {
      if (buttonKey(btn) === k) {
        btn.setAttribute('aria-pressed', favorited ? 'true' : 'false');
      }
    });
  }

  // Apply the loaded saved-set to every heart under root (idempotent: only sets
  // 'true' for saved keys; leaves the rest at their markup default of 'false').
  function applySaved(root) {
    if (!saved) return;
    $$('[data-fav-toggle]', root).forEach(function (btn) {
      if (saved[buttonKey(btn)]) btn.setAttribute('aria-pressed', 'true');
    });
  }

  function ensureLoaded() {
    if (loadState !== 'idle') return; // load exactly once
    loadState = 'loading';
    fetch(BASE, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        saved = {};
        (data && Array.isArray(data.favorites) ? data.favorites : []).forEach(
          function (f) {
            saved[key(f.entity_type, f.entity_id)] = true;
          }
        );
        loadState = 'done';
        applySaved(document);
      })
      .catch(function (err) {
        // Leave hearts outlined; toggling still works on click.
        loadState = 'error';
        console.error('[circadia] favourites initial load failed', err);
      });
  }

  // Trigger a lazy load when an authed heart is present, and paint whatever is
  // already loaded. Guest-only views never fetch (the GET would 401).
  function scan(root) {
    var hearts = $$('[data-fav-toggle]', root);
    if (hearts.length === 0) return;
    var hasAuthed = hearts.some(function (b) {
      return !b.hasAttribute('data-fav-guest');
    });
    if (hasAuthed) ensureLoaded();
    if (saved) applySaved(root);
  }

  function toggle(btn) {
    if (btn.hasAttribute('data-fav-guest')) {
      loginRedirect();
      return;
    }
    if (btn.getAttribute('aria-busy') === 'true') return;

    var type = btn.getAttribute('data-fav-type');
    var id = btn.getAttribute('data-fav-id');
    var k = key(type, id);
    var wasPressed = btn.getAttribute('aria-pressed') === 'true';
    var next = !wasPressed;

    // Optimistic flip across all hearts with this key.
    paintKey(k, next);
    setBusy(btn, true);

    fetch(BASE + '/toggle', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ entity_type: type, entity_id: id }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        // Reconcile from the server's authoritative new state, and keep the
        // saved-set current so a later re-scan won't undo this.
        var favorited = !!data.favorited;
        paintKey(k, favorited);
        if (saved) {
          if (favorited) saved[k] = true;
          else delete saved[k];
        }
      })
      .catch(function (err) {
        console.error('[circadia] favourites toggle failed', err);
        paintKey(k, wasPressed); // revert
      })
      .finally(function () {
        setBusy(btn, false);
      });
  }

  function boot() {
    scan(document);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fav-toggle]');
      if (!btn) return;
      // Hearts often sit inside or beside <a> wrappers (cards). Never navigate.
      e.preventDefault();
      e.stopPropagation();
      toggle(btn);
    });

    // Keyboard activation for span hearts (role="button"). Native <button>
    // hearts already fire a click on Enter/Space, so skip them to avoid a
    // double toggle.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var btn = e.target.closest('[data-fav-toggle]');
      if (!btn || btn.tagName === 'BUTTON') return;
      e.preventDefault();
      e.stopPropagation();
      toggle(btn);
    });

    // Re-scan when hearts are injected after load. The ONLY async-heart surface
    // is the JS-hydrated playbook archive ([data-pb-archive]), so observe just
    // that container (not document.body) and disconnect once it has hydrated —
    // it renders in a single innerHTML pass. Pages without it pay nothing.
    var asyncRoot = document.querySelector('[data-pb-archive]');
    if (asyncRoot && 'MutationObserver' in window) {
      var obs = new MutationObserver(function (mutations) {
        var relevant = false;
        for (var i = 0; i < mutations.length && !relevant; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (
              (node.matches && node.matches('[data-fav-toggle]')) ||
              (node.querySelector && node.querySelector('[data-fav-toggle]'))
            ) {
              relevant = true;
              break;
            }
          }
        }
        if (relevant) {
          scan(asyncRoot);
          obs.disconnect();
        }
      });
      obs.observe(asyncRoot, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
